import { useMutation } from "@tanstack/react-query";
import { sendEmailVerification } from "firebase/auth";
import * as Sentry from "@sentry/react";
import { useAuth, getClaim } from "@/hooks/use-auth";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

const checkVerificationStatus = async ({ user, navigate, locationState }) => {
    if (!user) {
        toast.error("User not found");
        throw new Error("User not found");
    }

    await user.reload();

    if (!user.emailVerified) {
        toast.error("Email has not been verified yet");
        throw new Error("Email has not been verified yet");
    }

    const claim = await getClaim(user);

    if (!claim) {
        const error = new Error("No role assigned");

        toast.error(error.message);
        Sentry.captureException(error);

        throw error;
    }

    if (locationState?.from === "booking") {
        navigate("/clinicians/book", { state: locationState.booking });
        return;
    }

    if (claim === "patient") {
        navigate("/patient/manage");
    } else if (claim === "clinician") {
        navigate("/clinician");
    }
};

export const useCheckVerification = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    return useMutation({
        mutationFn: () =>
            checkVerificationStatus({
                user,
                navigate,
                locationState: location.state,
            }),
        retry: false,
    });
};

const sendVerification = async ({ user }) => {
    if (!user) {
        toast.error("User not found");
        throw new Error("User not found");
    }

    try {
        await sendEmailVerification(user);
    } catch (err) {
        if (err.code === "auth/too-many-requests") {
            toast.error(
                "Too many attempts. Please wait a few minutes before trying again.",
            );
            throw err;
        }

        toast.error("Failed to send verification email.");
        Sentry.captureException(err);
        throw err;
    }
};

export const useSendVerification = () => {
    const { user } = useAuth();

    return useMutation({
        mutationFn: () => sendVerification({ user }),
        retry: false,
    });
};
